import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Pencil, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import type { IPDDischargeFollowUpItem } from '@/types/ipd';
import { useSearch } from '@/contexts/SearchContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  formatDateTime,
  formatIstTimeHHmm,
  istDateAndTimeToIso,
  toIstDateInputValue,
} from '@/utils/dateUtils';
import { ensureDoctorPrefix } from '@/lib/doctorName';

type FollowUpRange = 'today' | 'upcoming' | 'overdue';

function resolveFollowUpPayload(form: { followUpDate: string; followUpTime: string }) {
  const date = form.followUpDate.trim();
  const time = form.followUpTime.trim();
  if (date && !time) throw new Error('Follow-up time (IST) is required when date is set');
  if (!date && time) throw new Error('Follow-up date (IST) is required when time is set');
  if (!date || !time) throw new Error('Follow-up date and time (IST) are required');
  return istDateAndTimeToIso(date, time);
}

function filterItems(items: IPDDischargeFollowUpItem[], searchQuery: string) {
  if (!searchQuery.trim()) return items;
  const q = searchQuery.toLowerCase();
  return items.filter((item) => {
    const patient = item.admission.queue.patient;
    const doctor = item.admission.assignedDoctor;
    return (
      patient.name.toLowerCase().includes(q) ||
      patient.phone.toLowerCase().includes(q) ||
      (patient.uhid ?? '').toLowerCase().includes(q) ||
      (item.admission.queue.ipdNumber ?? '').toLowerCase().includes(q) ||
      doctor.name.toLowerCase().includes(q) ||
      (item.followUpReason ?? '').toLowerCase().includes(q)
    );
  });
}

export default function IpdDischargeFollowUpsPage() {
  const { searchQuery } = useSearch();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<FollowUpRange>('today');
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [selected, setSelected] = useState<IPDDischargeFollowUpItem | null>(null);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [followUpReason, setFollowUpReason] = useState('');

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['ipd-discharge-follow-ups', activeTab],
    queryFn: async () => {
      const res = await ipdApi.listDischargeFollowUps(activeTab);
      return res.data?.data ?? [];
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const filtered = useMemo(
    () => filterItems(items, searchQuery),
    [items, searchQuery]
  );

  const rescheduleMut = useMutation({
    mutationFn: async (payload: {
      admissionId: string;
      followUpAt: string;
      followUpReason: string | null;
    }) => {
      await ipdApi.rescheduleDischargeFollowUp(payload.admissionId, {
        followUpAt: payload.followUpAt,
        followUpReason: payload.followUpReason,
      });
    },
    onSuccess: async () => {
      toast.success('Follow-up rescheduled');
      setRescheduleOpen(false);
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['ipd-discharge-follow-ups'] });
      await queryClient.invalidateQueries({ queryKey: ['ipd-discharge-follow-up-summary'] });
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || (err instanceof Error ? err.message : 'Failed to reschedule'));
    },
  });

  const openReschedule = (item: IPDDischargeFollowUpItem) => {
    setSelected(item);
    setFollowUpDate(item.followUpAt ? toIstDateInputValue(item.followUpAt) : '');
    setFollowUpTime(item.followUpAt ? formatIstTimeHHmm(item.followUpAt) : '');
    setFollowUpReason(item.followUpReason ?? '');
    setRescheduleOpen(true);
  };

  const handleSaveReschedule = () => {
    if (!selected) return;
    try {
      const followUpAt = resolveFollowUpPayload({ followUpDate, followUpTime });
      rescheduleMut.mutate({
        admissionId: selected.admissionId,
        followUpAt,
        followUpReason: followUpReason.trim() || null,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid follow-up date/time');
    }
  };

  const renderTable = (rows: IPDDischargeFollowUpItem[]) => (
    <Table numberOfRows={10}>
      <TableHeader>
        <TableRow>
          <TableHead>Patient</TableHead>
          <TableHead>Phone</TableHead>
          <TableHead>IPD No.</TableHead>
          <TableHead>Doctor</TableHead>
          <TableHead>Follow-up (IST)</TableHead>
          <TableHead>Reason</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
              No follow-ups in this list
            </TableCell>
          </TableRow>
        ) : (
          rows.map((item) => {
            const patient = item.admission.queue.patient;
            const doctor = item.admission.assignedDoctor;
            return (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{patient.name}</TableCell>
                <TableCell>
                  <a href={`tel:${patient.phone}`} className="inline-flex items-center gap-1 text-primary">
                    <Phone className="h-3.5 w-3.5" />
                    {patient.phone}
                  </a>
                </TableCell>
                <TableCell>{item.admission.queue.ipdNumber ?? '—'}</TableCell>
                <TableCell>{ensureDoctorPrefix(doctor.name)}</TableCell>
                <TableCell>
                  {item.followUpAt ? (
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDateTime(item.followUpAt)} IST
                    </span>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="max-w-[200px] truncate" title={item.followUpReason ?? undefined}>
                  {item.followUpReason || '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => openReschedule(item)}>
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    Reschedule
                  </Button>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">IPD Discharge Follow-ups</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Post-discharge follow-ups from discharge summaries. All times are Indian Standard Time (IST).
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          Times shown in IST
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as FollowUpRange)}>
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming (30 days)</TabsTrigger>
          <TabsTrigger value="overdue">Overdue</TabsTrigger>
        </TabsList>

        {(['today', 'upcoming', 'overdue'] as const).map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-4">
            {isLoading && activeTab === tab ? (
              <div className="py-12 text-center text-muted-foreground">Loading follow-ups…</div>
            ) : (
              renderTable(activeTab === tab ? filtered : [])
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule follow-up</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Patient: <span className="font-medium text-foreground">{selected.admission.queue.patient.name}</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="reschedule-date">Follow-up date (IST)</Label>
                  <Input
                    id="reschedule-date"
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="reschedule-time">Follow-up time (IST)</Label>
                  <Input
                    id="reschedule-time"
                    type="time"
                    value={followUpTime}
                    onChange={(e) => setFollowUpTime(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="reschedule-reason">Reason for follow-up</Label>
                <Textarea
                  id="reschedule-reason"
                  value={followUpReason}
                  onChange={(e) => setFollowUpReason(e.target.value)}
                  rows={2}
                  className="mt-1"
                  placeholder="e.g. Suture removal, wound check"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveReschedule} disabled={rescheduleMut.isPending}>
              {rescheduleMut.isPending ? 'Saving…' : 'Save new date & time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
